import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { InsightsComponentRenderer } from "@/features/insights/components/InsightsComponentRenderer";
import { InsightsListCard } from "@/features/insights/components/InsightsListCard";
import { DocumentInfo } from "@/types/document";
import { InsightComponent, InsightsChatResponse } from "@/types/insights";
import { ArrowUp, RotateCcw, User } from "lucide-react";
import { FormEvent, useMemo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface InsightsResultsProps {
  activePrompt: string;
  error: string | null;
  result: InsightsChatResponse | null;
  documentsById: Map<string, DocumentInfo>;
  flaggingByDocumentId: Record<string, boolean>;
  followUpQuery: string;
  canSubmitFollowUp: boolean;
  onFlagDocument: (documentId: string) => Promise<void>;
  onFollowUpQueryChange: (value: string) => void;
  onFollowUp: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  onNewChat: () => void;
}

export const InsightsResults = ({
  activePrompt,
  error,
  result,
  documentsById,
  flaggingByDocumentId,
  followUpQuery,
  canSubmitFollowUp,
  onFlagDocument,
  onFollowUpQueryChange,
  onFollowUp,
  onNewChat,
}: InsightsResultsProps) => {
  const metricComponents = useMemo(
    () => result?.components.metrics ?? [],
    [result]
  );

  const insightItems = useMemo(
    () => result?.components.insights ?? [],
    [result]
  );

  const otherComponents = useMemo<InsightComponent[]>(
    () =>
      result
        ? [
            ...(result.components.barCharts ?? []),
            ...(result.components.pieCharts ?? []),
            ...(result.components.lineCharts ?? []),
            ...(result.components.tables ?? []),
          ]
        : [],
    [result]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hidden mx-auto max-w-3xl">
        <div className="pt-24 pb-8">
          <div className="flex items-start justify-between">
            <p>{activePrompt}</p>
            <Badge className="mt-2">
              User <User className="w-4 h-4" />
            </Badge>
          </div>

          <Separator className="mt-4 mb-6" />

          <div className="mt-2 mx-auto">
            <Badge className="mb-4 bg-purple-50 text-purple-600 border-purple-300">Claire AI</Badge>

            <div className="space-y-6">
              {!error && metricComponents.length > 0 && (
                <>
                  <div className="flex flex-wrap items-stretch gap-4">
                    {metricComponents.map((component, index) => (
                      <div key={`${component.type}-${index}`} className="flex h-full min-w-[10rem] shrink-0">
                        <InsightsComponentRenderer
                          component={component}
                          documentsById={documentsById}
                          flaggingByDocumentId={flaggingByDocumentId}
                          onFlagDocument={onFlagDocument}
                        />
                      </div>
                    ))}
                  </div>
                  <Separator />
                </>
              )}

              <div className="prose prose-sm max-w-none break-words [overflow-wrap:anywhere] prose-headings:text-foreground prose-headings:text-base prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground prose-a:text-primary">
                <Markdown remarkPlugins={[remarkGfm]}>
                  {error || result?.response || ""}
                </Markdown>
              </div>

              {!error && (insightItems.length > 0 || otherComponents.length > 0) && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    {insightItems.length > 0 ? (
                      <InsightsListCard insights={insightItems} />
                    ) : null}
                    {otherComponents.map((component, index) => (
                      <InsightsComponentRenderer
                        key={`${component.type}-${index}`}
                        component={component}
                        documentsById={documentsById}
                        flaggingByDocumentId={flaggingByDocumentId}
                        onFlagDocument={onFlagDocument}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="shrink-0 flex justify-center px-4 pb-10 pt-2">
        <form
          className="flex w-full max-w-2xl items-center gap-3 rounded-xl px-4 py-3 border shadow-xl"
          onSubmit={(e) => void onFollowUp(e)}
        >
          <Input
            value={followUpQuery}
            onChange={(e) => onFollowUpQueryChange(e.target.value)}
            placeholder="Ask a follow-up question"
            className="h-10 border-0 bg-transparent shadow-none focus-visible:ring-0"
          />
          <Button size="sm" type="submit" disabled={!canSubmitFollowUp}>
            Send
            <ArrowUp />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={onNewChat}
            aria-label="Start a new chat"
          >
            <RotateCcw className="size-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};
